import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../../../components/feature/Logo";

const NAV_KEYS = [
  { key: "nav.home",    href: "#home" },
  { key: "nav.services", href: "#services" },
  { key: "nav.custom",  href: "#custom-website" },
  { key: "nav.modules", href: "#modules" },
  { key: "nav.brand",   href: "#brand" },
  { key: "nav.contact", href: "#contact" },
];

const LANGS = [
  { code: "th", label: "TH" },
  { code: "en", label: "EN" },
  { code: "zh", label: "中文" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const ids = NAV_KEYS.map((n) => n.href.replace("#", ""));
      for (const id of [...ids].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveId(id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (href: string) => {
    document.getElementById(href.replace("#", ""))?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const currentLang = i18n.language?.startsWith("zh") ? "zh" : i18n.language?.startsWith("th") ? "th" : "en";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white border-b border-gray-100 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a href="#home" onClick={(e) => { e.preventDefault(); goto("#home"); }} className="cursor-pointer flex-shrink-0">
          <Logo variant={scrolled ? 'dark' : 'light'} size="md" />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {NAV_KEYS.map((item) => {
            const id = item.href.replace("#", "");
            const isActive = activeId === id;
            return (
              <a
                key={id}
                href={item.href}
                onClick={(e) => { e.preventDefault(); goto(item.href); }}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? isActive ? "text-[#C8953A]" : "text-[#1a1a1a]/55 hover:text-[#1a1a1a]"
                    : isActive ? "text-[#C8953A]" : "text-white/65 hover:text-white"
                }`}
              >
                {t(item.key)}
              </a>
            );
          })}
        </div>

        {/* Right: Lang switcher + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language switcher */}
          <div className={`flex items-center rounded-full p-0.5 text-xs font-semibold ${scrolled ? "bg-gray-100" : "bg-white/10"}`}>
            {LANGS.map((lang) => {
              const isActive = currentLang === lang.code;
              return (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer ${
                    isActive
                      ? "bg-[#C8953A] text-white"
                      : scrolled
                        ? "text-[#1a1a1a]/50 hover:text-[#1a1a1a]"
                        : "text-white/60 hover:text-white"
                  }`}
                >
                  {lang.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => goto("#contact")}
            className="whitespace-nowrap text-sm font-semibold px-5 py-2.5 rounded-full bg-[#C8953A] hover:bg-[#a97b30] text-white transition-all duration-200 cursor-pointer"
          >
            {t("nav.cta")}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? "text-[#1a1a1a]" : "text-white"}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-5 flex flex-col gap-4">
          {NAV_KEYS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => { e.preventDefault(); goto(item.href); }}
              className={`text-sm font-medium py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                activeId === item.href.replace("#", "") ? "text-[#C8953A]" : "text-[#1a1a1a]/60 hover:text-[#C8953A]"
              }`}
            >
              {t(item.key)}
            </a>
          ))}

          {/* Mobile lang switcher */}
          <div className="flex items-center gap-2 pt-1">
            {LANGS.map((lang) => {
              const isActive = currentLang === lang.code;
              return (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
                    isActive
                      ? "bg-[#C8953A] text-white border-[#C8953A]"
                      : "text-[#1a1a1a]/50 border-gray-200 hover:border-[#C8953A] hover:text-[#C8953A]"
                  }`}
                >
                  {lang.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => goto("#contact")}
            className="whitespace-nowrap bg-[#C8953A] hover:bg-[#a97b30] text-white text-sm font-semibold px-5 py-3 rounded-full mt-1 cursor-pointer"
          >
            {t("nav.cta")}
          </button>
        </div>
      )}
    </nav>
  );
}
